import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import '../../css/accounts/PendingInvoice.css';

const PendingInvoices = () => {
  const [pendingOrders, setPendingOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filter States
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [orderStatusFilter, setOrderStatusFilter] = useState('All');

  // Pagination States (15 items per page)
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  // Fetch Unpaid/Pending Express Orders from Firestore
  useEffect(() => {
    const q = query(collection(db, 'expressOrder'), orderBy('createdAt', 'desc'));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const records = snapshot.docs
        .map((doc) => {
          const data = doc.data();
          const payment = data.payment || {};
          const paymentStatus = payment.status || 'Unpaid';

          return {
            id: doc.id,
            createdAt: data.createdAt ? data.createdAt.toDate() : null,
            displayOrderId: data.displayOrderId || data.autoGeneratedId || doc.id,
            userName: data.userName || data.customerName || 'N/A',
            userPhone: data.userPhone || data.customerPhone || 'N/A',
            orderStatus: data.orderStatus || 'Pending',
            serviceQuality: data.serviceQuality || data.serviceCategory || 'Standard Express',
            deliveryFee: payment.deliveryFee !== undefined ? Number(payment.deliveryFee) : 0,
            tax: payment.tax !== undefined ? Number(payment.tax) : 0,
            subtotal: payment.subtotal !== undefined ? Number(payment.subtotal) : 0,
            totalPayable: payment.totalPayable !== undefined ? Number(payment.totalPayable) : 0,
            paymentStatus: paymentStatus,
          };
        })
        // Filter out completed payments -> Only show pending / unpaid / pending-pricing / COD
        .filter((item) => item.paymentStatus.toLowerCase() !== 'paid');

      setPendingOrders(records);
      setFilteredOrders(records);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching pending invoices: ", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Filter Logic
  useEffect(() => {
    let result = pendingOrders;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(item => 
        item.userName.toLowerCase().includes(term) ||
        item.userPhone.includes(term) ||
        item.displayOrderId.toLowerCase().includes(term)
      );
    }

    if (filterDate) {
      result = result.filter(item => {
        if (!item.createdAt) return false;
        const itemDate = item.createdAt.toISOString().split('T')[0];
        return itemDate === filterDate;
      });
    }

    if (orderStatusFilter !== 'All') {
      result = result.filter(item => item.orderStatus.toLowerCase() === orderStatusFilter.toLowerCase());
    }

    setFilteredOrders(result);
    setCurrentPage(1); // Reset pagination on search
  }, [searchTerm, filterDate, orderStatusFilter, pendingOrders]);

  // Outstanding Metrics Calculations
  const totalOutstanding = filteredOrders.reduce((sum, item) => sum + item.totalPayable, 0);
  const totalServicePending = filteredOrders.reduce((sum, item) => sum + item.subtotal, 0);
  const totalTaxPending = filteredOrders.reduce((sum, item) => sum + item.tax, 0);

  // Pagination Calculations
  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentDisplayedOrders = filteredOrders.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  return (
    <div className="pending-inv-container">
      <div className="pending-inv-card">
        
        {/* Header Section */}
        <div className="pending-header">
          <div>
            <h2 className="pending-title">Pending & Unpaid Invoices</h2>
            <p className="pending-subtitle">Track outstanding customer receivables and pending collections</p>
          </div>
          <div className="pending-badge">
            Unpaid Orders: <strong>{filteredOrders.length}</strong>
          </div>
        </div>

        {/* Pending Summary KPI Cards */}
        <div className="pending-kpi-grid">
          <div className="pending-kpi-card total-outstanding">
            <span className="pending-kpi-label">Total Outstanding</span>
            <span className="pending-kpi-value">AED {totalOutstanding.toFixed(2)}</span>
          </div>
          <div className="pending-kpi-card service-pending">
            <span className="pending-kpi-label">Pending Service Amount</span>
            <span className="pending-kpi-value">AED {totalServicePending.toFixed(2)}</span>
          </div>
          <div className="pending-kpi-card tax-pending">
            <span className="pending-kpi-label">Pending VAT Tax</span>
            <span className="pending-kpi-value">AED {totalTaxPending.toFixed(2)}</span>
          </div>
        </div>

        {/* Filter Toolbar */}
        <div className="pending-filter-bar">
          <div className="filter-group">
            <input 
              type="text" 
              placeholder="Search Invoice, Customer, Phone..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pending-input"
            />
            <input 
              type="date" 
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
              className="pending-input date-input"
            />
          </div>

          <div className="filter-group">
            <select 
              value={orderStatusFilter} 
              onChange={(e) => setOrderStatusFilter(e.target.value)}
              className="pending-select"
            >
              <option value="All">All Order Statuses</option>
              <option value="Pending">Pending</option>
              <option value="Assigned">Assigned</option>
              <option value="Accepted">Accepted</option>
              <option value="Picked Up">Picked Up</option>
              <option value="In-Transit">In-Transit</option>
              <option value="Delivered">Delivered</option>
            </select>
          </div>
        </div>

        {/* Pending Invoices Table */}
        <div className="pending-table-wrapper">
          <table className="pending-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Invoice ID</th>
                <th>Customer Name</th>
                <th>Customer Ph</th>
                <th>Order Status</th>
                <th>Service Quality</th>
                <th>Service Amt</th>
                <th>VAT</th>
                <th>Delivery Fee</th>
                <th>Total Pending</th>
                <th style={{ textAlign: 'center' }}>Payment Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="11" className="status-cell">Loading pending invoices...</td>
                </tr>
              ) : currentDisplayedOrders.length === 0 ? (
                <tr>
                  <td colSpan="11" className="status-cell">No pending or unpaid invoices found.</td>
                </tr>
              ) : (
                currentDisplayedOrders.map((item) => (
                  <tr key={item.id}>
                    <td>
                      {item.createdAt 
                        ? item.createdAt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                        : 'N/A'
                      }
                    </td>
                    <td className="invoice-id">{item.displayOrderId}</td>
                    <td className="customer-name">{item.userName}</td>
                    <td>{item.userPhone}</td>
                    <td>
                      <span className={`status-pill ${item.orderStatus.toLowerCase().replace(/\s+/g, '-')}`}>
                        {item.orderStatus}
                      </span>
                    </td>
                    <td>
                      <span className="category-chip">{item.serviceQuality}</span>
                    </td>
                    <td className="currency-cell">AED {item.subtotal.toFixed(2)}</td>
                    <td className="currency-cell">AED {item.tax.toFixed(2)}</td>
                    <td className="currency-cell">AED {item.deliveryFee.toFixed(2)}</td>
                    <td className="currency-cell pending-highlight">
                      AED {item.totalPayable.toFixed(2)}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <span className={`payment-pill ${item.paymentStatus.toLowerCase().replace(/\s+/g, '-')}`}>
                        {item.paymentStatus}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {!loading && filteredOrders.length > 0 && (
          <div className="pending-pagination-bar">
            <div className="pagination-info">
              Showing <strong>{indexOfFirstItem + 1}</strong> to <strong>{Math.min(indexOfLastItem, filteredOrders.length)}</strong> of <strong>{filteredOrders.length}</strong> pending records
            </div>

            <div className="pagination-buttons">
              <button 
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="page-btn nav-btn"
              >
                ◀ Previous
              </button>

              {Array.from({ length: totalPages }, (_, index) => index + 1)
                .filter(page => page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1)
                .map((page, idx, arr) => {
                  const prevPage = arr[idx - 1];
                  return (
                    <React.Fragment key={page}>
                      {prevPage && page - prevPage > 1 && <span className="pagination-dots">...</span>}
                      <button
                        onClick={() => handlePageChange(page)}
                        className={`page-btn ${currentPage === page ? 'active-page' : ''}`}
                      >
                        {page}
                      </button>
                    </React.Fragment>
                  );
                })}

              <button 
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="page-btn nav-btn"
              >
                Next ▶
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default PendingInvoices;